import type {Auth, Language, PortaroVersion} from 'typings/portaro.be.types';
import type {MenuItem} from 'shared/components/kp-main-menu/types';
import type {DefinedAction} from 'shared/defined-actions/types';
import type {TransitionService} from '@uirouter/core/lib/transition/transitionService';
import type {Transition} from '@uirouter/angularjs';
import type {Observable} from 'rxjs';
import type {CurrentPageDataService} from 'shared/page/current-page.data-service';
import type CurrentAuthService from 'shared/services/current-auth.service';
import type LoginService from 'shared/login/login.service';
import type {DefinedActionPerformer} from 'shared/defined-actions/defined-action-performer';
import {exists} from 'shared/utils/custom-utils';

type SidebarCurrentRouteListener = (routeUrl: string) => void;
type SidebarToggleCollapseListener = (collapsed: boolean) => void;

export class ErpSidebarNavService {
    public static serviceName = 'erpSidebarService';

    private listeners: SidebarToggleCollapseListener[] = [];

    /*@ngInject*/
    constructor(private $transitions: TransitionService,
                private loginService: LoginService,
                private currentPageDataService: CurrentPageDataService,
                private definedActionPerformer: DefinedActionPerformer,
                private currentAuthService: CurrentAuthService,
                public readonly portaroVersion: PortaroVersion,
                public readonly switchableLanguages: Language[]) {
    }

    public registerCurrentRouteListener(callback: SidebarCurrentRouteListener): () => void {
        const unsubscribeFunc = this.$transitions.onSuccess({}, (transition: Transition) => {
            callback(transition.to().url.split('?')[0]);
        });

        return () => unsubscribeFunc();
    }

    public registerToggleCollapseListener(callback: SidebarToggleCollapseListener): () => void {
        this.listeners.push(callback);

        return () => {
            this.listeners = this.listeners.filter((filteredCallback) => filteredCallback !== callback);
        }
    }

    public toggleCollapse(collapsed: boolean): void {
        for (const listener of this.listeners) {
            listener(collapsed);
        }
    }

    public async loadCurrentMenuItems(): Promise<MenuItem[]> {
        try {
            return await this.currentPageDataService.getCurrentPageMenuItems();
        } catch {
            return window.mainMenu.items;
        }
    }

    public async performClick(menuItem: MenuItem): Promise<void> {
        await this.checkUserAuth(menuItem);
        await this.performAction(menuItem.action);
    }

    public getCurrentAuth$(): Observable<Auth> {
        return this.currentAuthService.currentAuth$();
    }

    public getMenuItems(): MenuItem[] {
        return window.mainMenu.items;
    }

    private async checkUserAuth(menuItem: MenuItem): Promise<void> {
        if (!menuItem.requiresAuth || this.currentAuthService.isLoggedIn()) {
            return;
        }

        await this.loginService.thisPageLogged();
    }

    private async performAction(action: DefinedAction | null): Promise<void> {
        if (!exists(action)) {
            return;
        }

        return this.definedActionPerformer.performAction(action);
    }
}